import { Box, Typography } from "@mui/material";
import { ResolvedColors } from "../colorResolver";

interface ILineupPreviewProps {
  colors: ResolvedColors;
  images: { thumbnail?: string };
  likedStarColor: string;
}

const DAYS = ["8 Jul", "9 Jul", "10 Jul", "11 Jul"];
const DAY_NAMES = ["WED", "THU", "FRI", "SAT"];
const BANDS = [
  { name: "Split Dogs", time: "14:00 - 14:30", stage: "Forest", liked: false },
  { name: "Love Rarely", time: "14:35 - 15:05", stage: "Word", liked: true },
  { name: "Karen Dió", time: "15:10 - 15:40", stage: "Forest", liked: false },
];

export const LineupPreview = ({ colors, images, likedStarColor }: ILineupPreviewProps) => {
  return (
    <Box sx={{ width: "100%", height: "100%", backgroundColor: colors.primaryColorDarker, overflow: "hidden" }}>
      {/* Native stack header - always primaryColorDarker, not one of the derived lineup colors */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
        }}
      >
        <Box sx={{ width: 18, height: 14, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ height: 2, borderRadius: 1, backgroundColor: colors.headerButtonColor }} />
          ))}
        </Box>
        <Typography sx={{ color: colors.primaryColorText, fontWeight: 800, fontSize: 18 }}>
          Lineup
        </Typography>
        <Typography sx={{ color: colors.primaryColorText, fontSize: 18, lineHeight: 1 }}>★</Typography>
      </Box>

      {/* Day selector */}
      <Box sx={{ display: "flex", justifyContent: "space-around", backgroundColor: colors.headerColor, py: 1 }}>
        {DAYS.map((date, i) => (
          <Box key={date} sx={{ textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: colors.dateColor, display: "block", fontSize: 9 }}>
              {date}
            </Typography>
            <Typography sx={{ color: colors.dayColor, fontWeight: 700, fontSize: 13 }}>
              {DAY_NAMES[i]}
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                height: 2,
                width: 24,
                mx: "auto",
                backgroundColor: i === 0 ? colors.selectedDayUnderlineColor : colors.dayUnderlineColor,
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Search bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: colors.searchColor,
          px: 1.5,
          py: 1,
        }}
      >
        <Typography sx={{ color: colors.primaryColorText, fontSize: 14 }}>⇄</Typography>
        <Typography variant="caption" sx={{ color: colors.primaryColorText, opacity: 0.7, flex: 1 }}>
          Search...
        </Typography>
        <Typography sx={{ color: colors.primaryColorText, fontSize: 14 }}>⊗</Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.rowGroupColor,
          px: 1.5,
          py: 0.5,
        }}
      >
        <Typography variant="caption" sx={{ color: colors.rowGroupTextColor, fontWeight: 700 }}>
          WEDNESDAY
        </Typography>
      </Box>

      {BANDS.map((band, i) => (
        <Box key={band.name}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              backgroundColor: colors.rowColor,
              px: 1.5,
              py: 1,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                backgroundColor: colors.primaryColorDarker,
                backgroundImage: images.thumbnail ? `url(${images.thumbnail})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ color: colors.primaryColorText, fontWeight: 700, fontSize: 13 }}>
                {band.name}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.primaryColorText, display: "block", fontSize: 10 }}>
                {band.time}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.primaryColorText, fontSize: 10, opacity: 0.85 }}>
                {band.stage}
              </Typography>
            </Box>
            <Typography
              sx={{
                color: band.liked ? likedStarColor : colors.primaryColorText,
                fontSize: 20,
                lineHeight: 1,
              }}
            >
              {band.liked ? "★" : "☆"}
            </Typography>
          </Box>
          {i < BANDS.length - 1 && (
            <Box sx={{ height: "1.5px", backgroundColor: colors.itemSeperatorBorder }} />
          )}
        </Box>
      ))}
    </Box>
  );
};
